import { z } from 'zod';

import { emailSchema } from '@front/common/src/forms/validators/email-schema';
import { firstNameSchema } from '@front/common/src/forms/validators/first-name-schema';
import { lastNameSchema } from '@front/common/src/forms/validators/last-name-schema';
import { phoneValidation } from '@front/common/src/forms/validators/phone/phone';
import { salutationSchema } from '@front/common/src/forms/validators/salutation-schema';
import { Address, User } from '@front/common/src/types/commons';
import { validateAddress } from '@front/common/src/forms/validators/address';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { passwordSchema } from '@front/common/src/forms/validators/password-schema';
import { Country } from '@front/common/src/types/country';

import { translate } from '@front/i18n';

const t = translate('screens.signup');

export type SignupFormType = z.infer<ReturnType<typeof getSignupFormSchema>>;

type GetInitialValuesArgs = {
  partialUser?: Partial<User>;
  countryCode: Country;
  dialCode: string;
};

export const getInitialValues = ({ partialUser, countryCode, dialCode }: GetInitialValuesArgs): SignupFormType => ({
  address: {
    address: partialUser?.address ?? '',
    city: partialUser?.city ?? '',
    country_code: countryCode,
    lat: partialUser?.lat ?? '',
    lng: partialUser?.lng ?? '',
    street_name: partialUser?.street_name ?? '',
    street_number: partialUser?.street_number ?? '',
    zip_code: partialUser?.zip_code ?? '',
  },
  addressError: '',
  countryCode,
  dialCode,
  email: partialUser?.email ?? '',
  first_name: partialUser?.first_name ?? '',
  last_name: partialUser?.last_name ?? '',
  password: partialUser?.password ?? '',
  phoneNumber: partialUser?.phone ?? '',
  salutation: partialUser?.salutation ?? '',
  userInputAddress: '',
});

// Typing `country_code` with Formik is tricky.
// Ideally, it should be of type `Country`, but we use `string` for simplicity.
// Since `Address` expects `Country` instead of `string`, we need to fix the type at some point.
// This function ensures `country_code` is correctly typed.
export const refineAddressType = (address: Omit<Address, 'country_code'> & { country_code: string }) => ({
  ...address,
  country_code: getCurrentCountry(),
});

export const getSignupFormSchema = (withSocialLogin: boolean, basketAddress?: Address) =>
  z
    .object({
      address: z.object({
        address: z.string(),
        city: z.string(),
        country_code: z.string(),
        lat: z.union([z.string(), z.number()]),
        lng: z.union([z.string(), z.number()]),
        street_name: z.string(),
        street_number: z.string(),
        zip_code: z.string(),
      }),
      addressError: z.string(),
      countryCode: z.string(),
      dialCode: z.string(),
      email: emailSchema,
      first_name: firstNameSchema,
      last_name: lastNameSchema,
      password: withSocialLogin ? z.string().optional() : passwordSchema,
      phoneNumber: z.string(),
      salutation: salutationSchema,
      userInputAddress: z.string(),
    })
    .superRefine(({ userInputAddress, address }, ctx) => {
      if (!validateAddress(basketAddress) && userInputAddress.length === 0 && !address.address) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('inputs.address.errors.required'),
          path: ['addressError'],
        });
      } else if (!validateAddress(basketAddress) && !validateAddress(refineAddressType(address))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('inputs.address.errors.invalid'),
          path: ['addressError'],
        });
      }
    })
    .superRefine((value, ctx) => {
      if (!value.phoneNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('inputs.phone.errors.required'),
          path: ['phoneNumber'],
        });
      } else if (
        !phoneValidation({ countryCode: value.countryCode, dialCode: value.dialCode, phoneNumber: value.phoneNumber })
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('inputs.phone.errors.invalid'),
          path: ['phoneNumber'],
        });
      }
    });
